import type { IconName } from '~/content/types';
import { cn } from '~/lib/cn';
import { CurrencyFlag, type CurrencyCode } from './CurrencyFlag';

/** Flag icon names render the reusable circular flag badge instead of an SVG asset. */
const flagIcon: Partial<Record<IconName, CurrencyCode>> = {
  'flag-usd': 'USD',
  'flag-gbp': 'GBP',
  'flag-eur': 'EUR',
};

/**
 * Maps the semantic icon names used across content to the shared SVG icon assets
 * in /public/icons (exported from the design system).
 */
const iconFile: Partial<Record<IconName, string>> = {
  'at-sign': 'at-sign',
  'arrow-down': 'chevron-down',
  'arrow-right': 'external-link',
  bank: 'bank',
  bell: 'bell',
  bitcoin: 'bitcoin',
  calculator: 'analytics',
  card: 'card',
  chart: 'bar-chart',
  check: 'check',
  'chevron-down': 'chevron-down',
  clock: 'clock',
  close: 'close',
  coin: 'dollar-circle',
  copy: 'copy',
  divide: 'divide',
  dollar: 'dollar',
  earth: 'earth',
  download: 'download',
  globe: 'globe',
  help: 'help',
  invoice: 'file',
  lock: 'lock',
  percent: 'percent',
  phone: 'phone',
  'phone-vibrate': 'phone-vibrate',
  search: 'search',
  send: 'transfer',
  settings: 'settings',
  shield: 'shield',
  shuffle: 'shuffle',
  snowflake: 'snowflake',
  star: 'star',
  swap: 'exchange',
  trend: 'trend',
  upload: 'upload',
  user: 'user',
  users: 'users',
  wallet: 'wallet',
  zap: 'refresh',
};

export function Icon({
  name,
  className = 'size-6',
  tint = false,
}: {
  name: IconName;
  className?: string;
  /**
   * When true, the icon is rendered as a CSS mask filled with `currentColor`
   * instead of an `<img>`, so it inherits (and transitions with) the surrounding
   * text color. Used where an icon needs to change color on hover/active state.
   */
  tint?: boolean;
}) {
  const flag = flagIcon[name];
  if (flag) {
    return <CurrencyFlag code={flag} className={className} />;
  }
  const src = `/icons/${iconFile[name]}.svg`;
  if (tint) {
    return (
      <span
        aria-hidden="true"
        className={cn('inline-block bg-current', className)}
        style={{
          maskImage: `url(${src})`,
          WebkitMaskImage: `url(${src})`,
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskPosition: 'center',
          maskSize: 'contain',
          WebkitMaskSize: 'contain',
        }}
      />
    );
  }
  return <img src={src} alt="" aria-hidden="true" className={cn('object-contain', className)} />;
}
